
import { Prop, Watch } from 'vue-property-decorator';
import { Options, Vue } from 'vue-class-component';
import { CompanyClient, ProductionPhaseConfigurationClient, RawMaterialClient } from '@/services/Services';
import OverrideWaterFootprintForGarmentModal from './overriderWaterFootprintForGarmentModal.vue';
import InfoModal from '@/components/modals/infoModal.vue';
import * as OM from '@/Model';

@Options({})
export default class EditModal extends Vue {

    @Prop({
        default: () => new OM.RawMaterialConfigurationVM()
    }) model: OM.RawMaterialConfigurationVM;
    localModel: OM.RawMaterialConfigurationVM = new OM.RawMaterialConfigurationVM();

    garmentIds: OM.TextIdentifier[] = [];
    rawMatIds: OM.TextIdentifier[] = [];
    categories: string[] = [];
    companies: OM.Company[] = [];
    productionPhaseIds: OM.TextIdentifier[] = [];

    newWaterFootprintRow: OM.WaterFootprint = new OM.WaterFootprint();
    opened: string[] = [];
    processKeys: string[] = [];

    @Prop() callback: any;

    created() {
        this.localModel = {...this.model};
        // The spread is shallow. Every mutable member below is edited in place by this modal, so without
        // an explicit copy the modal writes straight into the row in the table behind it — Cancel or no
        // Cancel. canSave now depends on garmentsUsage, so this stopped being cosmetic.
        this.localModel.emissionFactorFootwearType = {...this.model.emissionFactorFootwearType};
        this.localModel.garmentsUsage = [...(this.model.garmentsUsage || [])];
        this.localModel.companiesUsage = [...(this.model.companiesUsage || [])];
        this.localModel.waterFootprints = (this.model.waterFootprints || []).map(x => ({...x}));
        this.localModel.parent = {...this.model.parent};

        if (this.isProcessBased) {
            RawMaterialClient.getRequiredProcessTypes(this.localModel.materialType, false)
            .then(x => {
                this.processKeys = x;
                // A material created from the Footwear tab arrives with an empty dictionary; give every
                // required key a slot so v-model has something to bind to.
                x.forEach(key => {
                    if (this.localModel.emissionFactorFootwearType[key] == null)
                        this.localModel.emissionFactorFootwearType[key] = null;
                });
            })
            .catch(y => {
                // canSave requires processKeys.length > 0 for every footwear/bag material, and processKeys
                // is populated exclusively from this call. If it fails, the modal renders zero emission
                // fields and Save stays disabled with no explanation — the material genuinely cannot be
                // edited here, so close the modal instead of leaving the admin stuck in a dead form.
                this.$opModal.show(InfoModal, {
                    text: this.$localizationService.getLocalizedValue("Error", "Error"),
                    subtitle: this.$localizationService.getLocalizedValue(
                        "Could not load the emission factor fields for this material, so it cannot be edited right now.",
                        "Could not load the emission factor fields for this material, so it cannot be edited right now."),
                    deny: this.$localizationService.getLocalizedValue("Close", "Close"),
                    denyCb: () => {
                        this.$opModal.closeAll();
                    }
                })
            })
        }

        Promise.all([
            RawMaterialClient.getGarmentsForMaterialType(this.localModel.materialType),
            RawMaterialClient.getAllConfigurations(this.localModel.materialType),
            RawMaterialClient.getAllCategories(),
            CompanyClient.getAll(),
            ProductionPhaseConfigurationClient.getAllConfigurations()
        ])
        .then(xs => {
            xs[0].forEach(element => {
                var garment = new OM.TextIdentifier();
                garment.identifier = element.identifier;
                garment.text = element.text;
                this.garmentIds.push(garment);
            });

            xs[1].forEach(element => {
                var newTexId = new OM.TextIdentifier();
                newTexId.identifier = element.identifier;
                newTexId.text = element.materialName;
                this.rawMatIds.push(newTexId);
            });

            this.categories = xs[2];
            this.companies = xs[3];

            var rawMatId = new OM.TextIdentifier();
            rawMatId.identifier = "00000000-0000-0000-0000-000000000000";
            rawMatId.text = "Raw Material";
            this.productionPhaseIds.push(rawMatId);

            xs[4].forEach(element => {
                var phaseId = new OM.TextIdentifier();
                phaseId.identifier = element.identifier;
                phaseId.text = element.productionPhase;
                this.productionPhaseIds.push(phaseId);
            });
        })
    }

    get defaultWaterFootprints() {
        return this.localModel.waterFootprints.filter(x => x.garment == null);
    }
    get groupedWaterFootprintsByGarment() {
        return this.localModel.waterFootprints
            .filter(wf => wf.garment && wf.garment.text)
            .reduce((groups, wf) => {
                const key = wf.garment.text;
                if (!groups[key]) 
                    groups[key] = [];
                groups[key].push(wf);
                return groups;
            }, {} as Record<string, OM.WaterFootprint[]>);
    }

    // MaterialType.Apparel is 0, so `if (this.localModel.materialType)` is FALSE for an apparel material.
    // Compare explicitly.
    get isProcessBased() {
        return this.localModel.materialType == OM.MaterialType.Footwear
            || this.localModel.materialType == OM.MaterialType.Bag;
    }

    get canSave() {
        // Trimmed here so every material type is covered: the server uses IsNullOrWhiteSpace, and without
        // this a footwear/bag/fabric material named "   " sailed past this guard (only the apparel branch
        // below used to re-check with .trim()) and 400'd on save.
        if (!this.localModel.materialName || !this.localModel.materialName.trim())
            return false;

        if (this.isProcessBased) {
            // An empty garmentsUsage means "every garment": the material would land in the apparel catalog.
            return this.processFactorsValid && this.localModel.garmentsUsage.length > 0;
        }

        if (this.localModel.materialType == OM.MaterialType.Fabric)
            return true;

        // The number input emits a STRING, so a plain !! would accept a negative cO2PerGrams (server
        // demands > 0). ">" 0 works on both string and number inputs alike ("2.62" > 0 is true, "-5" > 0
        // is false).
        return !!this.localModel.suggestedGramsPerSquareMeters
            && this.localModel.cO2PerGrams > 0;
    }

    // A footwear or bag material never reads cO2PerGrams or suggestedGramsPerSquareMeters — it is priced
    // off EmissionFactorFootwearType[processType]. Demanding them is exactly why no footwear material has
    // ever been editable here.
    //
    // Every required key must carry a positive number: GetAllAvailableMaterialsForGarment filters on
    // ContainsKey(processType), so a material missing "Vulcanized" silently disappears from the picker
    // when that process is chosen, and the estimation path reads the dictionary with a bare indexer and
    // throws. Shared with the template so the disabled Save button can explain itself instead of just
    // sitting there dead.
    get processFactorsValid() {
        return this.processKeys.length > 0
            && this.processKeys.every(key => this.localModel.emissionFactorFootwearType[key] > 0);
    }

    toggleWaterFootprintAccordion(garment: string) {
        if (this.opened.includes(garment)) {
            this.opened = this.opened.filter(g => g !== garment);
        } else {
            this.opened.push(garment);
        }
    }

    toggleGarment(garmentId: string) {
        var existingIndex = this.localModel.garmentsUsage.indexOf(garmentId);
        if(existingIndex == -1)
            this.localModel.garmentsUsage.push(garmentId);
        else
            this.localModel.garmentsUsage.splice(existingIndex, 1);
    }
    toggleCompany(companyId: string) {
        var existingIndex = this.localModel.companiesUsage.indexOf(companyId);
        if(existingIndex == -1)
            this.localModel.companiesUsage.push(companyId);
        else
            this.localModel.companiesUsage.splice(existingIndex, 1);
    }

    get remainingPhasesIIds() {
        return this.productionPhaseIds.filter(x => this.localModel.waterFootprints.findIndex(y => y.productionPhase.identifier == x.identifier || (!y.productionPhase.identifier && x.text == "Raw Material")) == -1)
    }

    addWaterFootprintRow() {
        var phaseName = this.productionPhaseIds.filter(x => x.identifier == this.newWaterFootprintRow.productionPhase.identifier)[0];
        this.newWaterFootprintRow.productionPhase.text = phaseName.text;
        this.localModel.waterFootprints.push({...this.newWaterFootprintRow});

        var garmentsWithOverride = this.localModel.waterFootprints.map(x => x.garment != null && x.garment.identifier);
        garmentsWithOverride.forEach(garmentId => {
            var filteredListByGarment = this.localModel.waterFootprints.filter(x => x.garment != null && x.garment.identifier == garmentId);
            var alreadyExist = filteredListByGarment.findIndex(x => x.productionPhase.identifier == phaseName.identifier) > -1;
            if(!alreadyExist) {
                var newGarmentWaterFootprintRow = new OM.WaterFootprint();
                newGarmentWaterFootprintRow = {...this.newWaterFootprintRow};
                newGarmentWaterFootprintRow.garment = this.garmentIds.filter(x => x.identifier == garmentId)[0];
                this.localModel.waterFootprints.push(newGarmentWaterFootprintRow);
            }
        });

        this.newWaterFootprintRow = new OM.WaterFootprint();
    }
    removeWaterFootprintRow(index: number) {
        this.localModel.waterFootprints.splice(index, 1);
    }

    overrideWaterFootprintDefaultConfig(selectedGarmentName: string) {
        var garmentAlreadyUsed = this.localModel.waterFootprints.map(x => x.garment != null && x.garment.identifier);
        var garmentIds = this.garmentIds.filter(x => garmentAlreadyUsed.indexOf(x.identifier) == -1);

        var selectedGarmentId = '';
        if(selectedGarmentName) {
            var selectedGarmentTextId = this.garmentIds.filter(x => x.text == selectedGarmentName)[0];
            selectedGarmentId = selectedGarmentTextId.identifier;
            garmentIds.push({...selectedGarmentTextId});
        }

        this.$opModal.show(OverrideWaterFootprintForGarmentModal, {
            selectedGarmentId: selectedGarmentId,
            waterFootprintList: [...this.localModel.waterFootprints],
            garmentIds: garmentIds,
            callback: (waterFootprintListWithGarment: OM.WaterFootprint[]) => {
                if(selectedGarmentName) {
                    for (let i = this.localModel.waterFootprints.length - 1; i >= 0; i--) {
                        const wf = this.localModel.waterFootprints[i];
                        if (wf.garment && wf.garment.text === selectedGarmentName) {
                            this.localModel.waterFootprints.splice(i, 1);
                        }
                    }
                }

                waterFootprintListWithGarment.forEach(element => {
                    this.localModel.waterFootprints.push({...element});
                });

                this.$opModal.closeLast();
            }
        })
    }
    removeWaterFootprintOvveride(garment: string) {
        for (let i = this.localModel.waterFootprints.length - 1; i >= 0; i--) {
            const wf = this.localModel.waterFootprints[i];
            if (wf.garment && wf.garment.text === garment) {
                this.localModel.waterFootprints.splice(i, 1);
            }
        }
    }

    save() {
        if(!this.canSave)
            return;

        this.callback(this.localModel);
    }
    
}
